/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 13th 2022, 12:48:23 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { getBrowserObjectStorageFunctions } from "lib/utils";
import { useGlobalScope } from "core/providers";
import { Organization, User } from "lib/schemas";

export interface IOnboardingState {
  onboardingStep: number;
  isReadyToSubscribe: boolean;
}

interface IOnboardingStateLocalStorage {
  [key: User["id"]]: { [key: Organization["id"]]: { onboardingState: Pick<IOnboardingState, "onboardingStep"> } };
}

interface IOnboardingContext {
  onboardingState: IOnboardingState;
  setOnboardingState: React.Dispatch<React.SetStateAction<IOnboardingState>>;
  handleNextStep: () => void;
}

export const OnboardingContext = React.createContext<IOnboardingContext>({
  onboardingState: null,
  setOnboardingState: null,
  handleNextStep: null,
});

const getOnboardingStateFromBrowser = (
  loggedInUserId: User["id"],
  currentOrganization: Organization
): IOnboardingStateLocalStorage[User["id"]][Organization["id"]]["onboardingState"] => {
  if (!loggedInUserId) return;

  if (!currentOrganization?.id || currentOrganization?.organization_status !== "pending") return;

  const { getObjectFromBrowserStorage } = getBrowserObjectStorageFunctions<IOnboardingStateLocalStorage>();

  const { onboardingState } =
    getObjectFromBrowserStorage("onestop.scope.onboardingState")?.[loggedInUserId]?.[currentOrganization.id] ?? {};

  return onboardingState;
};

const saveOnboardingStateToBrowser = (
  loggedInUserId: User["id"],
  currentOrganization: Organization,
  onboardingState: IOnboardingStateLocalStorage[User["id"]][Organization["id"]]["onboardingState"]
): void => {
  if (!loggedInUserId) return;

  if (!currentOrganization?.id || currentOrganization?.organization_status !== "pending") return;

  const { getObjectFromBrowserStorage, saveObjectToBrowserStorage } =
    getBrowserObjectStorageFunctions<IOnboardingStateLocalStorage>();

  const stateFromBrowser = getObjectFromBrowserStorage("onestop.scope.onboardingState");

  saveObjectToBrowserStorage("onestop.scope.onboardingState", {
    ...stateFromBrowser,
    [loggedInUserId]: { ...stateFromBrowser?.[loggedInUserId], [currentOrganization.id]: { onboardingState } },
  });
};

export const OnboardingProvider = ({ children }) => {
  const {
    globalState: { selectedOrganization },
    userContext: { loggedInUser },
  } = useGlobalScope();

  const onboardingStateFromBrowser = getOnboardingStateFromBrowser(loggedInUser?.id, selectedOrganization); // Getting step from localstorage so that step can remain even on browser refresh

  const [onboardingState, setOnboardingState] = React.useState<IOnboardingState>({
    onboardingStep: onboardingStateFromBrowser?.onboardingStep ?? 0,
    isReadyToSubscribe: false,
  });

  React.useEffect(() => {
    // if loggedInUser or organization changes, update the onboarding state from the browser
    if (typeof onboardingStateFromBrowser?.onboardingStep === "number") {
      setOnboardingState((prev) => ({ ...prev, onboardingStep: onboardingStateFromBrowser.onboardingStep }));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser?.id, selectedOrganization?.id]);

  React.useEffect(() => {
    saveOnboardingStateToBrowser(loggedInUser?.id, selectedOrganization, {
      onboardingStep: onboardingState.onboardingStep,
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingState.onboardingStep, loggedInUser?.id]);

  const resetOnboardingState = () => {
    saveOnboardingStateToBrowser(loggedInUser?.id, selectedOrganization, null);
    setOnboardingState({
      onboardingStep: 0,
      isReadyToSubscribe: false,
    });
  };

  React.useEffect(() => {
    const { organization_status } = selectedOrganization ?? {};

    if (organization_status === "active") resetOnboardingState();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization?.organization_status]);

  React.useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization?.department_set?.length > 0 &&
      selectedOrganization?.portals?.length > 0
    ) {
      setOnboardingState((prev) => ({ ...prev, isReadyToSubscribe: true }));
      return;
    }

    setOnboardingState((prev) => ({ ...prev, isReadyToSubscribe: false }));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onboardingState.onboardingStep,
    selectedOrganization?.department_set?.length,
    selectedOrganization?.portals?.length,
  ]);

  const handleNextStep = () => setOnboardingState((prev) => ({ ...prev, onboardingStep: prev.onboardingStep + 1 }));

  return (
    <OnboardingContext.Provider value={{ onboardingState, setOnboardingState, handleNextStep }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = () => {
  return React.useContext(OnboardingContext);
};
